/**
 * TimeWindowPicker — the "only alerts between 8 AM and 8 PM" control.
 *
 * One component for both entry points on a report page: the Time Range section of the
 * Report Settings drawer, and the time-range popover in the header. Sharing it is the
 * point — two copies of the option lists would eventually offer different boundaries
 * for the same stored value.
 *
 * Applies immediately, like the date-range buttons it sits beside, rather than waiting
 * for the drawer's Apply. That is deliberate: the header popover has no Apply button,
 * and one value that commits at different moments depending on where it was set is a
 * worse trap than a filter that always commits at once.
 *
 * Value is {start, end} in minutes from midnight. The parent owns it; this only reads
 * and reports changes. See utils/reportTimeWindow.js for the semantics, including why
 * start > end is a legitimate overnight window.
 */

import {
    DEFAULT_TIME_WINDOW, DAY_MINUTES, describeTimeWindow, normalizeTimeWindow,
    timeWindowStartOptions, timeWindowEndOptions,
} from '../../utils/reportTimeWindow.js';

const TimeWindowPicker = ({ value, onChange, compact = false }) => {
    const win = normalizeTimeWindow(value);
    const label = describeTimeWindow(win.start, win.end);

    const emit = (next) => onChange?.(normalizeTimeWindow(next));

    return (
        <div className={`tw-picker${compact ? ' tw-picker--compact' : ''}`}>
            <div className="tw-picker__row">
                <select
                    className="tw-picker__select"
                    value={win.start}
                    onChange={(e) => emit({ start: parseInt(e.target.value, 10), end: win.end })}
                    aria-label="Time of day window start"
                >
                    {timeWindowStartOptions().map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
                <span className="tw-picker__sep">to</span>
                <select
                    className="tw-picker__select"
                    value={win.end}
                    onChange={(e) => emit({ start: win.start, end: parseInt(e.target.value, 10) })}
                    aria-label="Time of day window end"
                >
                    {timeWindowEndOptions().map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
                {label ? (
                    <button
                        type="button"
                        className="tw-picker__reset"
                        onClick={() => emit(DEFAULT_TIME_WINDOW)}
                        title="Include events at any hour"
                    >
                        Reset
                    </button>
                ) : null}
            </div>
            {/* States what the window means rather than leaving the reader to work it out
                from two selects — especially for an overnight window, which is easy to
                read as an empty range. */}
            <p className="tw-picker__hint">
                {label
                    ? (win.start > win.end
                        ? `${label} — each day includes midnight to ${describeTimeWindow(0, win.end).split(' – ')[1]} and ${describeTimeWindow(win.start, DAY_MINUTES).split(' – ')[0]} to midnight`
                        : label)
                    : 'All day — events at any hour are included'}
            </p>
        </div>
    );
};

export default TimeWindowPicker;
